import React, { useState } from "react";
import { Upload, Button } from "antd";
import { Notification } from "../common/Notification";
import * as services from "../../store/service";
import ReputationScannerTestResult from "./ReputationScannerTestResult";
import PresetProfile from "./PresetProfile";
import { IProfile } from "../../store/repIQPresetProfile/types";
import RenderIcons from "../../Assets/css/_icons/partner_icon/RenderIcons";

const ReputationScanner = (props: any) => {
    const [showResultSection, setShowResultSection] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [analysisResult, setAnalysisResult] = useState({} as any);
    const [file, setFile] = useState({} as any);
    const [fileName, setFileName] = useState("");
    const [isAnalysing, setIsAnalysing] = useState(false);
    const [selectedPresetProfile, setSelectedPresetProfile] = useState({} as IProfile);
    const [resetProfile, setResetProfile] = useState(1);

    const uploadFile = async (data: any) => {
        if (!validateFile(data.file)) {
            return;
        }
        setFileName(data.file.name);
        setFile(data.file);
    };

    const toBase64 = (file: any) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () =>
                resolve(
                    reader.result
                        ? reader.result
                              .toString()
                              .replace(/^data:;base64,/, "")
                              .toString()
                              .replace(/^data:.+;base64,/, "")
                        : ""
                );
            reader.onerror = (error) => reject(error);
        });

    const validateFile = (file: any): boolean => {
        let isValid = true;
        if (file.size > 10240000) {
            isValid = false;
            Notification({
                type: "error",
                title: "Error",
                message: "File size can not be more than 10MB.",
                duration: 5,
            });
        } else if (!file.name.toLowerCase().endsWith(".txt") && !file.name.toLowerCase().endsWith(".pdf") && !file.name.toLowerCase().endsWith(".docx")) {
            isValid = false;
            Notification({
                type: "error",
                title: "Error",
                message: "Only the following file types are supported: TXT, PDF or DOCX.",
                duration: 5,
            });
        }

        return isValid;
    };

    const testButton = async () => {
        if (fileName !== "") {
            setIsAnalysing(true);

            const fileContent = await toBase64(file);

            const requestBody = {
                fileContent: fileContent,
                label: fileName,
            };

            setIsUploading(true);
            setShowResultSection(true);
            services
                .getAnalysisResult(requestBody)
                .then((data: any) => {
                    let errorText = "";

                    if (data && data.hasError && data.errorText !== "") errorText = data.errorText;
                    else errorText = "Something went wrong, Please try again.";

                    if (data && data.hasError === false) {
                        setIsUploading(false);
                        setAnalysisResult(data);
                        setIsAnalysing(false);
                    } else {
                        setIsUploading(false);
                        setShowResultSection(false);
                        Notification({
                            type: "error",
                            title: "Error",
                            message: errorText,
                            duration: 5,
                        });
                    }
                })
                .catch(function (error: any) {
                    setIsUploading(false);
                    setShowResultSection(false);
                    Notification({
                        type: "error",
                        title: "Error",
                        message: "Something went wrong, Please try again.",
                        duration: 5,
                    });
                });
        } else {
            Notification({
                type: "error",
                title: "Error",
                message: "Please upload a text file first",
                duration: 5,
            });
        }
    };

    const resetButton = () => {
        setShowResultSection(false);
        setFileName("");
        setResetProfile(resetProfile + 1);
    };

    const updateSelectedPresetProfile = (profile: IProfile) => {
        setSelectedPresetProfile(profile);
    };

    return (
        <React.Fragment>
            <div className="box offset-padding">
                <div className="box-header">
                    <div className="box-title-container">
                        <div className="box-title-icon" aria-hidden="true">
                            <RenderIcons iconName="file-alt" />
                        </div>
                        <div className="box-title-text">
                            <h2 className="box-title">Reputation Scanner</h2>
                        </div>
                    </div>
                </div>
                <div className="box-content">
                    <h3 className="content-sub-title">Upload an article, post or essay and incorporate it into your reputation analysis.</h3>
                    <p>
                        Utilize the Reputation Scanner to review a specific piece of content to analyze how it may be perceived.
                        <br />
                        Select a preset profile to analyze your reputation against a target benchmark.
                    </p>

                    <PresetProfile updateSelectedPresetProfile={updateSelectedPresetProfile} isSmallVersion={false} resetProfile={resetProfile} />

                    <p className="small_text">
                        * The Reputation Scanner requires content with a{" "}
                        <strong>
                            <em style={{ textDecoration: "underline" }}>minimum of 100 words</em>
                        </strong>
                        . For more accurate analysis, upload content with at least 600 words.
                    </p>
                    <p className="small_text">* The Reputation Scanner supports only the following file types: TXT, PDF and DOCX.</p>
                    <div className="margin-vertical-l"></div>

                    <div className="file-upload">
                        <label className="file-upload-label">
                            {fileName !== "" ? (
                                <p>{fileName}</p>
                            ) : (
                                <React.Fragment>
                                    <span className="upload-icon">
                                        <em className="las la-cloud-upload-alt"></em>
                                    </span>
                                    <Upload disabled={isUploading} customRequest={uploadFile} showUploadList={false} accept={".txt,.pdf,.docx"}>
                                        <Button className="btn-default btn-primary" disabled={isUploading}>
                                            {isUploading ? "Uploading..." : "Upload a file"} {isUploading && <i className="las la-circle-notch la-spin"></i>}
                                        </Button>
                                    </Upload>
                                </React.Fragment>
                            )}
                        </label>
                    </div>

                    <div className="margin-top-l">
                        <ul className="list-reset list-inline">
                            <li className="margin-right-xs">
                                <button type="button" onClick={testButton} className="btn-default btn-primary btn-min-width">
                                    <span className="btn-text">Test</span>
                                </button>
                            </li>
                            <li>
                                <button type="button" onClick={resetButton} className="btn-default btn-primary-outline btn-min-width">
                                    <span className="btn-text">Reset All</span>
                                </button>
                            </li>
                        </ul>
                    </div>

                    <div className="margin-vertical-l"></div>
                    {showResultSection && (
                        <ReputationScannerTestResult data={analysisResult} isAnalysing={isAnalysing} selectedPresetProfile={selectedPresetProfile} />
                    )}
                </div>
            </div>
        </React.Fragment>
    );
};
export default ReputationScanner;
